import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import Seo from "@/components/Seo";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters.");
    if (password !== confirm) return toast.error("Passwords don't match.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated. Please log in.");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex-1 grid place-items-center px-5 py-12">
      <Seo title="Set new password — MeasureTheRoof" description="Set a new password for your MeasureTheRoof account." path="/reset-password" noindex />
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6"><Logo /></div>
        <div className="card-mtr">
          <h1 className="font-body font-extrabold text-navy text-[24px] mb-1">Set a new password</h1>
          <p className="text-mortar text-[14px] mb-6">Choose a strong password you'll remember.</p>
          {!token ? (
            <p className="text-caution-red text-[14px]" data-testid="reset-invalid">This reset link is invalid or missing a token.</p>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label htmlFor="pw" className="label-mtr">New password</label>
                <div className="relative">
                  <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-mortar" aria-hidden="true" />
                  <input id="pw" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="input-mtr pl-11" placeholder="At least 8 characters" data-testid="reset-password" />
                </div>
              </div>
              <div>
                <label htmlFor="cpw" className="label-mtr">Confirm password</label>
                <div className="relative">
                  <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-mortar" aria-hidden="true" />
                  <input id="cpw" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="input-mtr pl-11" placeholder="Re-enter password" data-testid="reset-confirm" />
                </div>
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="reset-submit">
                {loading ? <Loader2 className="animate-spin" size={18} /> : "Update password"}
              </button>
            </form>
          )}
        </div>
        <p className="text-center text-[14px] text-mortar mt-5">
          <Link to="/login" className="text-orange font-bold">Back to log in</Link>
        </p>
      </div>
    </div>
  );
}
